import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CircleCheck, Users, SortAsc, Trash2, AlertCircle, Menu } from "lucide-react";
import { toast, Toaster } from "react-hot-toast";
import Sidebar from "../Components/Sidebar";
import TopBar from "../Components/TopBar";
import BottomBar from "../Components/BottomBar";
import { getAllElections } from "../services/ElectionService";
import { getVotersByElection, deleteVoters } from "../services/VoterService";
import VotersList from "../Components/VotersList";

const Voters = () => {
    const [voters, setVoters] = useState([]);
    const [elections, setElections] = useState([]);
    const [selectedElection, setSelectedElection] = useState("");
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectedVoters, setSelectedVoters] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [sidebarExpanded, setSidebarExpanded] = useState(window.innerWidth >= 768);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchElections();

        // Handle responsive sidebar on window resize
        const handleResize = () => {
            setSidebarExpanded(window.innerWidth >= 768);
        };

        window.addEventListener("resize", handleResize);

        const handleSidebarToggle = (e) => {
            if (e.detail && typeof e.detail.expanded === "boolean") {
                setSidebarExpanded(e.detail.expanded);
            }
        };

        window.addEventListener("sidebarToggle", handleSidebarToggle);

        return () => {
            window.removeEventListener("sidebarToggle", handleSidebarToggle);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const fetchElections = async () => {
        setIsLoading(true);
        try {
            const response = await getAllElections();
            setElections(response || []);
        } catch {
            toast.error("Failed to load elections");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchVoters = async (electionId) => {
        setIsLoading(true);
        try {
            const data = await getVotersByElection(electionId);
            setVoters(data);
        } catch {
            toast.error("Failed to load voter details.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedElection) {
            fetchVoters(selectedElection);
        }
    }, [selectedElection]);

    const handleDeleteSelectedDB = async () => {
        if (selectedVoters.length === 0) {
            toast.error("Please select voters to delete.");
            return;
        }
        try {
            await deleteVoters(selectedVoters);
            toast.success("Selected voters deleted successfully.");
            fetchVoters(selectedElection);
            setSelectedVoters([]);
            setIsSelecting(false);
            setSelectAll(false);
        } catch {
            toast.error("Failed to delete voters.");
        }
    };

    const handleElectionChange = (e) => {
        setSelectedElection(e.target.value);
    };

    const handleSelectClick = () => {
        setIsSelecting(!isSelecting);
        setSelectedVoters([]);
        setSelectAll(false);
    };

    const handleCheckboxChange = (voterId) => {
        setSelectedVoters((prev) =>
            prev.includes(voterId)
                ? prev.filter((id) => id !== voterId)
                : [...prev, voterId]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedVoters([]);
        } else {
            setSelectedVoters(voters.map((voter) => voter.id));
        }
        setSelectAll(!selectAll);
    };

    // Updated content class for better centering when sidebar is minimized
    const contentClass = sidebarExpanded
        ? "sm:ml-64 w-full sm:w-[calc(100%-16rem)]"
        : "ml-16 w-[calc(100%-4rem)] md:w-[calc(100%-4rem)] mx-auto flex justify-center";

    // Inner container class for centering content
    const innerContainerClass = !sidebarExpanded
        ? "w-full max-w-4xl"
        : "w-full";

    const EmptyState = () => (
        <div className="flex flex-col items-center justify-center p-4 md:p-10 text-center">
            <div className="bg-blue-50 rounded-full p-4 mb-4">
                <AlertCircle size={30} className="text-blue-500 md:w-10 md:h-10" />
            </div>
            <h3 className="text-lg md:text-xl font-semibold mb-2">No Voters Found</h3>
            <p className="text-gray-600 mb-4 md:mb-6 max-w-md text-sm md:text-base">
                Create your first voter to start managing your election.
            </p>
            <Link
                to="/VoterRegistration"
                className="bg-blue-500 text-white px-4 py-2 md:px-6 md:py-3 rounded-full flex items-center hover:bg-blue-600 transition shadow-md text-sm md:text-base"
            >
                Add New Voter <CircleCheck className="ml-2" size={window.innerWidth < 768 ? 16 : 20} />
            </Link>
        </div>
    );

    const LoadingSpinner = () => (
        <div className="flex justify-center items-center p-6 md:p-10">
            <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
    );

    return (
        <div className="flex flex-col h-screen bg-bgColor relative">
            <TopBar>
                <button
                    onClick={() => setSidebarExpanded(!sidebarExpanded)}
                    className="p-2 mr-2 md:hidden"
                    aria-label="Toggle sidebar"
                >
                    <Menu size={24} />
                </button>
            </TopBar>
            <Sidebar
                onToggle={(expanded) => setSidebarExpanded(expanded)}
                isOpen={sidebarExpanded}
                onClose={() => setSidebarExpanded(false)}
            />
            <div className={`flex-1 transition-all duration-300 ease-in-out mt-16 ${contentClass}`}>
                <div className={`p-3 md:p-10 h-[calc(100vh-4rem)] overflow-auto ${innerContainerClass}`}>
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 space-y-3 md:space-y-0">
                        <div className="mt-2 md:mt-5">
                            <div className="flex items-center space-x-2">
                                <h2 className="text-xl md:text-2xl font-bold">Voters</h2>
                                <div className="flex items-center text-lg md:text-2xl font-bold text-gray-600">
                                    <Users className="w-5 h-5 md:w-6 md:h-6 mr-1 stroke-[2]" />
                                    <span>{voters?.length || 0}</span>
                                </div>
                            </div>
                        </div>
                        <select
                            value={selectedElection}
                            onChange={handleElectionChange}
                            className="border p-2 rounded-md shadow-md text-gray-700 w-full md:w-auto text-sm md:text-base"
                        >
                            <option value="">-- Select Election --</option>
                            {elections.map((election) => (
                                <option key={election.id} value={election.id}>
                                    {election.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="bg-white rounded-2xl shadow-lg w-full p-2 md:p-3">
                        {isLoading ? (
                            <LoadingSpinner />
                        ) : voters.length > 0 ? (
                            <>
                                {/* Header: Left "Select All", Right action buttons */}
                                <div className="flex flex-col sm:flex-row justify-between items-center p-2 border-b space-y-2 sm:space-y-0">
                                    <div className="flex items-center">
                                        {isSelecting && (
                                            <>
                                                <span className="text-xs md:text-sm mr-2">Select All</span>
                                                <div
                                                    className={`w-4 h-4 md:w-5 md:h-5 flex justify-center items-center rounded-full border-2 cursor-pointer ${
                                                        selectAll ? "bg-blue-500 border-blue-500 text-white" : "border-gray-400"
                                                    }`}
                                                    onClick={handleSelectAll}
                                                >
                                                    {selectAll && <CircleCheck size={16} className="text-white" />}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={handleSelectClick}
                                            className="text-blue-700 border border-blue-500 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-blue-100 hover:bg-blue-500 hover:text-white transition text-xs md:text-sm"
                                        >
                                            {isSelecting ? "Cancel" : "Select"}
                                        </button>
                                        {isSelecting && (
                                            <button
                                                onClick={handleDeleteSelectedDB}
                                                className="text-red-600 border border-red-500 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-red-100 hover:bg-red-500 hover:text-white transition flex items-center text-xs md:text-sm"
                                            >
                                                <Trash2 size={14} className="mr-1" /> Delete
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {}}
                                            className="text-gray-600 border border-gray-400 px-2 py-1 md:px-3 md:py-1 rounded-xl bg-gray-200 hover:bg-gray-400 hover:text-white transition flex items-center text-xs md:text-sm"
                                        >
                                            <SortAsc size={14} className="mr-1" /> Sort
                                        </button>
                                    </div>
                                </div>
                                {/* Voters List */}
                                <div className="space-y-2 md:space-y-4 mt-2 md:mt-4 overflow-y-auto max-h-[50vh] md:max-h-[500px] custom-scrollbar">
                                    {voters.map((voter) => (
                                        <div
                                            key={voter.id}
                                            className="cursor-pointer flex items-center justify-between py-2 px-2 md:p-3 border-b w-full"
                                            onDoubleClick={() =>
                                                navigate("/VoterRegistration", { state: voter })
                                            }
                                        >
                                            <div className="flex items-center w-full">
                                                {isSelecting && (
                                                    <div
                                                        className={`w-4 h-4 md:w-5 md:h-5 flex justify-center items-center rounded-full border-2 cursor-pointer mr-2 md:mr-3 ${
                                                            selectedVoters.includes(voter.id)
                                                                ? "bg-blue-500 border-blue-500 text-white"
                                                                : "border-gray-400"
                                                        }`}
                                                        onClick={() => handleCheckboxChange(voter.id)}
                                                    >
                                                        {selectedVoters.includes(voter.id) && (
                                                            <CircleCheck size={16} className="text-white" />
                                                        )}
                                                    </div>
                                                )}
                                                <VotersList
                                                    image={voter.image || voter.photoUrl}
                                                    name={voter.name}
                                                    designation={voter.designation}
                                                    voter={voter}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* Bottom Action Buttons: "+ Add Voter" on left, "Confirm" on right */}
                                <div className="flex flex-col sm:flex-row items-center justify-between mt-4 space-y-3 sm:space-y-0 px-2">
                                    <Link to="/VoterRegistration" className="text-blue-500 text-xs md:text-sm self-start">
                                        + Add Voter
                                    </Link>
                                </div>
                            </>
                        ) : (
                            <EmptyState />
                        )}
                    </div>
                </div>
            </div>
            <BottomBar />
            <Toaster />
        </div>
    );
};

export default Voters;